/**
 * Talent Directory — mockup §6463-6664, adapted to v2 schema.
 *
 * v2 removed availability / SME / activeDemands / completedDemands /
 * lastActive (see docs/V2_ARCHITECTURE.md §9). This page now renders
 * only fields the v2 user model + user_skills join actually carry:
 *   - avatar (initials) + name
 *   - role + FA badge
 *   - skill chips (with skill-filter highlight)
 *   - "Invite" CTA
 *
 * Filters: search, focus area, skill. Sort: name, most skills.
 */

import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Body1,
  Button,
  Caption1,
  Combobox,
  Dropdown,
  Input,
  Option,
  Spinner,
  Subtitle2,
  Title2,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  DismissRegular,
  People24Regular,
  PersonAdd20Regular,
  Search20Regular,
} from '@fluentui/react-icons'
import { useTalentDirectory, type Talent } from '../hooks/useTalentDirectory'
import { useInviteTalents, useDemand } from '../hooks/useDemands'
import { useToast } from '../components/ToastProvider'
import { TitleWithHelp } from '../components/TitleWithHelp'
import { FOCUS_AREAS } from '../types/focus_area'
import { bayerColors } from '../theme'
import { apiErrorMessage } from '../lib/apiError'
import { activatable } from '../lib/activatable'

type SortKey = 'name' | 'most-skills'

const SORT_LABELS: Record<SortKey, string> = {
  name: 'Sort: Name',
  'most-skills': 'Most skills',
}

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  head: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalM,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  searchWrap: { flex: '1 1 240px', minWidth: '200px' },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  card: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingHorizontalM,
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    transitionProperty: 'box-shadow, border-color',
    transitionDuration: '120ms',
    ':hover': {
      borderTopColor: '#54749a',
      borderRightColor: '#54749a',
      borderBottomColor: '#54749a',
      borderLeftColor: '#54749a',
      boxShadow: tokens.shadow8,
    },
  },
  cardSelected: {
    borderTopColor: bayerColors.navy,
    borderRightColor: bayerColors.navy,
    borderBottomColor: bayerColors.navy,
    borderLeftColor: bayerColors.navy,
    boxShadow: `0 0 0 1px #46647e inset, ${tokens.shadow4}`,
  },
  selectBox: {
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    border: `1.5px solid ${tokens.colorNeutralStroke1}`,
    background: tokens.colorNeutralBackground1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: bayerColors.navy,
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: 1,
    flexShrink: 0,
    transitionProperty: 'background, border-color',
    transitionDuration: '100ms',
    ':hover': { background: bayerColors.navyLight },
  },
  selectBoxChecked: {
    background: bayerColors.navy,
    borderTopColor: bayerColors.navy,
    borderRightColor: bayerColors.navy,
    borderBottomColor: bayerColors.navy,
    borderLeftColor: bayerColors.navy,
    color: '#fff',
  },
  bulkBar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    background: bayerColors.navyLight,
    border: `1px solid #46647e`,
    borderRadius: tokens.borderRadiusMedium,
    color: bayerColors.navy,
    fontWeight: 600,
    fontSize: '13px',
  },
  bulkSpacer: { flex: 1 },
  cardHead: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  avatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: bayerColors.navyLight,
    color: bayerColors.navy,
    fontWeight: 700,
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  info: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
    flex: 1,
  },
  name: {
    fontWeight: 600,
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  meta: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
  },
  chip: {
    fontSize: '11px',
    padding: '2px 8px',
    background: '#f0f0f0',
    color: tokens.colorNeutralForeground2,
    borderRadius: '12px',
  },
  chipMatch: {
    background: bayerColors.navyLight,
    color: bayerColors.navy,
    fontWeight: 600,
  },
  inviteBtn: {
    opacity: 0,
    transitionProperty: 'opacity',
    transitionDuration: '120ms',
    position: 'absolute',
    top: tokens.spacingHorizontalS,
    right: tokens.spacingHorizontalS,
  },
  cardInviteVisible: {
    ':hover [data-talent-invite]': { opacity: 1 },
    ':focus-within [data-talent-invite]': { opacity: 1 },
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
  },
  engagement: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '2px',
  },
  engBadge: {
    fontSize: '11px',
    fontWeight: 500,
    padding: '2px 8px',
    borderRadius: '4px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '3px',
  },
  engApp: {
    background: bayerColors.navyLight,
    color: bayerColors.navy,
  },
  engInv: {
    background: '#fff3e0',
    color: '#8a5100',
  },
})

function initials(name: string): string {
  const parts = name.replace(/^\[TEST\]\s*/i, '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return (parts[0] ?? '').slice(0, 2).toUpperCase() || '?'
  const first = parts[0] ?? ''
  const last = parts[parts.length - 1] ?? first
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase() || '?'
}

// Talents choose from FA1-FA4, as on the profile page (BO is council-only, UN
// is intake's holding code).
const TALENT_FA_CODES = ['FA1', 'FA2', 'FA3', 'FA4']
const FA_OPTIONS = FOCUS_AREAS.filter((f) => TALENT_FA_CODES.includes(f.code)).map((f) => ({
  code: f.code,
  label: `${f.code} · ${f.name}`,
}))

// The backend's rule (services/focus_area_preferences.py): preferences are
// opt-out, so an empty list takes in every FA. Not `focus_area_code` — that is
// council membership, NULL for talents, so filtering on it matched nobody.
function prefersFocusArea(talent: Talent, code: string): boolean {
  const prefs = talent.preferred_focus_areas ?? []
  return prefs.length === 0 || prefs.includes(code)
}

// Built from the same rule as the filter, so the card shows what it matches.
function focusAreasLabel(talent: Talent): string {
  if (TALENT_FA_CODES.every((code) => prefersFocusArea(talent, code))) return 'All FAs'
  return [...(talent.preferred_focus_areas ?? [])].sort().join(', ')
}

interface TalentCardProps {
  talent: Talent
  skillFilter: string
  selected: boolean
  onToggleSelect: (id: number) => void
}

function TalentCard({ talent, skillFilter, selected, onToggleSelect }: TalentCardProps) {
  const styles = useStyles()
  const navigate = useNavigate()
  const shownSkills = talent.skills.slice(0, 5)
  const extraCount = talent.skills.length - shownSkills.length
  // Keep the raw `[TEST] ` prefix in the displayed name — that's the
  // single source-of-truth flag for test rows. No extra chip needed.
  const metaBits: string[] = []
  if (talent.role) metaBits.push(talent.role)
  metaBits.push(focusAreasLabel(talent))
  if (talent.job_title) metaBits.push(talent.job_title)
  if (talent.department) metaBits.push(talent.department)
  if (talent.country) metaBits.push(talent.country)
  const metaText = metaBits.join(' · ')

  const openDetail = () => navigate(`/talents/${talent.id}`)

  return (
    <article
      className={mergeClasses(
        styles.card,
        selected && styles.cardSelected,
      )}
      data-testid={`talent-card-${talent.id}`}
      role="button"
      {...activatable(openDetail)}
      style={{ cursor: 'pointer' }}
      aria-label={`Open profile for ${talent.name}`}
    >
      <div className={styles.cardHead}>
        <div
          role="checkbox"
          aria-checked={selected}
          aria-label={selected ? `Deselect ${talent.name}` : `Select ${talent.name} for invitation`}
          tabIndex={0}
          className={mergeClasses(styles.selectBox, selected && styles.selectBoxChecked)}
          onClick={(e) => {
            e.stopPropagation()
            onToggleSelect(talent.id)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              onToggleSelect(talent.id)
            }
          }}
          data-testid={`talent-select-${talent.id}`}
        >
          {selected ? '✓' : ''}
        </div>
        <div className={styles.avatar} aria-hidden="true">
          {initials(talent.name)}
        </div>
        <div className={styles.info}>
          <div className={styles.name}>{talent.name}</div>
          <div className={styles.meta} title={metaText}>{metaText || '—'}</div>
        </div>
      </div>
      {talent.bio && (
        <p
          style={{
            margin: 0,
            fontSize: '13px',
            color: tokens.colorNeutralForeground2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {talent.bio}
        </p>
      )}
      {((talent.applications_in_fa?.length ?? 0) > 0 || (talent.invitations_in_fa?.length ?? 0) > 0) && (
        <div className={styles.engagement}>
          {(talent.applications_in_fa ?? []).map((app, i) => (
            <span key={`app-${i}`} className={mergeClasses(styles.engBadge, styles.engApp)}>
              📋 {app.demand_code ?? app.demand_title} · {app.status}
            </span>
          ))}
          {(talent.invitations_in_fa ?? []).map((inv, i) => (
            <span key={`inv-${i}`} className={mergeClasses(styles.engBadge, styles.engInv)}>
              ✉️ Invited → {inv.demand_code ?? inv.demand_title}
            </span>
          ))}
        </div>
      )}
      <div className={styles.chips}>
        {shownSkills.length === 0 && (
          <Caption1 style={{ color: tokens.colorNeutralForegroundDisabled }}>
            No skills listed yet
          </Caption1>
        )}
        {shownSkills.map((skill) => (
          <span
            key={skill}
            className={mergeClasses(
              styles.chip,
              skill === skillFilter && styles.chipMatch,
            )}
          >
            {skill}
          </span>
        ))}
        {extraCount > 0 && <span className={styles.chip}>+{extraCount}</span>}
      </div>
    </article>
  )
}

export function TalentDirectory() {
  const styles = useStyles()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data, isLoading, isError, error } = useTalentDirectory()

  const [search, setSearch] = useState('')
  const [faFilter, setFaFilter] = useState('')
  // Arrives pre-filtered from the skills catalog's usage panel (`?skill=`).
  const [skillFilter, setSkillFilter] = useState(searchParams.get('skill') ?? '')
  const [sortBy, setSortBy] = useState<SortKey>('name')
  const [selected, setSelected] = useState<Set<number>>(new Set())

  // ── Invite-for-demand mode (arriving from Demand Detail) ───────────────
  const inviteForId = searchParams.get('inviteFor')
  const { data: inviteForDemand } = useDemand(inviteForId ?? undefined)
  const inInviteForMode = !!inviteForId
  const inviteTalents = useInviteTalents()
  const { showToast } = useToast()
  // Note field is no longer surfaced in this dialog (other session removed it);
  // keep an empty value so the existing invite mutation signature is happy.
  const [inviteNote] = useState('')

  const cancelInviteFor = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.delete('inviteFor')
      return next
    })
    setSelected(new Set())
  }

  const confirmInviteFor = () => {
    if (!inviteForId || selected.size === 0) return
    inviteTalents.mutate(
      { demandId: Number(inviteForId), talentIds: Array.from(selected), note: inviteNote.trim() || undefined },
      {
        onSuccess: (res) => {
          showToast({
            title: `Invited ${res.invited.length} of ${res.total_requested}`,
            body:
              res.skipped_already_invited.length > 0
                ? `Skipped ${res.skipped_already_invited.length} already-invited talents.`
                : undefined,
            intent: 'success',
          })
          navigate(`/demand/${inviteForId}`)
        },
        onError: (err) => {
          showToast({ title: 'Invite failed', body: apiErrorMessage(err), intent: 'error' })
        },
      },
    )
  }

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }
  const clearSelection = () => setSelected(new Set())
  const bulkInvite = () => {
    if (selected.size === 0) return
    const ids = Array.from(selected).join(',')
    navigate(`/demands?invite_talents=${ids}`)
  }

  const talents = useMemo(() => data?.items ?? [], [data])

  const allSkills = useMemo(
    () => Array.from(new Set(talents.flatMap((t) => t.skills))).sort(),
    [talents],
  )

  const filtered = useMemo(() => {
    let list = talents
    if (search) {
      const q = search.toLowerCase()
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          (t.role ?? '').toLowerCase().includes(q) ||
          t.skills.some((s) => s.toLowerCase().includes(q)),
      )
    }
    if (faFilter) list = list.filter((t) => prefersFocusArea(t, faFilter))
    if (skillFilter) list = list.filter((t) => t.skills.includes(skillFilter))
    const sorted = [...list].sort((a, b) =>
      sortBy === 'most-skills'
        ? b.skills.length - a.skills.length
        : a.name.localeCompare(b.name),
    )
    return sorted
  }, [talents, search, faFilter, skillFilter, sortBy])

  const hasFilters = !!(search || faFilter || skillFilter)

  const handleClear = () => {
    setSearch('')
    setFaFilter('')
    setSkillFilter('')
  }

  if (isLoading) {
    return (
      <div className={styles.page}>
        <Spinner label="Loading talents..." />
      </div>
    )
  }
  if (isError) {
    return (
      <div className={styles.page}>
        <Body1>Failed to load the talent directory. {apiErrorMessage(error)}</Body1>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <div>
          {/* Fluent text components render inline by default, and JSX drops
              the newline between siblings — without `block` the caption
              concatenates straight onto the title ("Talent Directory216 of
              216 talents"). Found by the visual-QA shot review. */}
          <TitleWithHelp topic="talentDirectory">
            <Title2 as="h1" block>
              Talent Directory
            </Title2>
          </TitleWithHelp>
          <Caption1 block>
            {filtered.length} of {talents.length} talents
          </Caption1>
        </div>
      </header>

      {inInviteForMode && (
        <div
          role="region"
          aria-label="Invite mode"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.spacingHorizontalM,
            padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
            background: bayerColors.navyLight,
            borderTopWidth: '1px',
            borderRightWidth: '1px',
            borderBottomWidth: '1px',
            borderLeftWidth: '1px',
            borderTopStyle: 'solid',
            borderRightStyle: 'solid',
            borderBottomStyle: 'solid',
            borderLeftStyle: 'solid',
            borderTopColor: bayerColors.navy,
            borderRightColor: bayerColors.navy,
            borderBottomColor: bayerColors.navy,
            borderLeftColor: bayerColors.navy,
            borderRadius: tokens.borderRadiusMedium,
            color: bayerColors.navy,
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          <span>
            Inviting to <strong>{inviteForDemand?.title ?? `demand #${inviteForId}`}</strong>
            {' — '}select talents below, then confirm. Tells each person invited, by email and in the app.
          </span>
          <div style={{ flex: 1 }} />
          <Button size="small" appearance="subtle" onClick={cancelInviteFor}>
            Cancel
          </Button>
        </div>
      )}

      {selected.size > 0 && (
        <div className={styles.bulkBar} role="region" aria-label="Selected talents">
          <span>
            {selected.size} talent{selected.size !== 1 ? 's' : ''} selected
          </span>
          <div className={styles.bulkSpacer} />
          {inInviteForMode ? (
            <Button
              size="small"
              appearance="primary"
              icon={<PersonAdd20Regular />}
              onClick={confirmInviteFor}
              disabled={inviteTalents.isPending}
              data-testid="talent-confirm-invite"
            >
              {inviteTalents.isPending ? 'Inviting...' : `Invite ${selected.size} to this demand`}
            </Button>
          ) : (
            <Button
              size="small"
              appearance="primary"
              icon={<PersonAdd20Regular />}
              onClick={bulkInvite}
              data-testid="talent-bulk-invite"
            >
              Invite to a demand
            </Button>
          )}
          <Button
            size="small"
            appearance="subtle"
            onClick={clearSelection}
            data-testid="talent-bulk-clear"
          >
            Clear selection
          </Button>
        </div>
      )}

      <div className={styles.toolbar} role="toolbar" aria-label="Talent filters">
        <div className={styles.searchWrap}>
          <Input
            type="search"
            placeholder="Search by name, role, or skill..."
            value={search}
            onChange={(_, d) => setSearch(d.value)}
            contentBefore={<Search20Regular />}
            aria-label="Search talents"
          />
        </div>
        <Combobox
          placeholder="All focus areas"
          value={faFilter}
          selectedOptions={faFilter ? [faFilter] : []}
          onOptionSelect={(_, d) => setFaFilter(d.optionValue ?? '')}
          aria-label="Filter by focus area"
        >
          <Option value="">All focus areas</Option>
          {FA_OPTIONS.map((fa) => (
            <Option key={fa.code} value={fa.code}>
              {fa.label}
            </Option>
          ))}
        </Combobox>
        <Combobox
          placeholder="All skills"
          value={skillFilter}
          selectedOptions={skillFilter ? [skillFilter] : []}
          onOptionSelect={(_, d) => setSkillFilter(d.optionValue ?? '')}
          aria-label="Filter by skill"
        >
          <Option value="">All skills</Option>
          {allSkills.map((skill) => (
            <Option key={skill} value={skill}>
              {skill}
            </Option>
          ))}
        </Combobox>
        <Dropdown
          value={SORT_LABELS[sortBy]}
          selectedOptions={[sortBy]}
          onOptionSelect={(_, d) => {
            if (d.optionValue) setSortBy(d.optionValue as SortKey)
          }}
          aria-label="Sort talents"
        >
          {(Object.keys(SORT_LABELS) as SortKey[]).map((key) => (
            <Option key={key} value={key}>
              {SORT_LABELS[key]}
            </Option>
          ))}
        </Dropdown>
        {hasFilters && (
          <Button
            appearance="subtle"
            size="small"
            icon={<DismissRegular />}
            onClick={handleClear}
            data-testid="talent-clear-filters"
          >
            Clear
          </Button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className={styles.empty} data-testid="talent-empty">
          <People24Regular fontSize={48} />
          <Subtitle2>No talents match your filters</Subtitle2>
          <Caption1>Try adjusting the search or clearing filters.</Caption1>
        </div>
      ) : (
        <div className={styles.grid} data-testid="talent-grid">
          {filtered.map((t) => (
            <TalentCard
              key={t.id}
              talent={t}
              skillFilter={skillFilter}
              selected={selected.has(t.id)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default TalentDirectory
